import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const HiringNotesContext = createContext();

export const useHiringNotes = () => {
    const context = useContext(HiringNotesContext);
    if (!context) {
        throw new Error('useHiringNotes must be used within a HiringNotesProvider');
    }
    return context;
};

export const HiringNotesProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isNotesVisible, setIsNotesVisible] = useState(false);

    // Parse URL parameters to determine initial state
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const notesParam = urlParams.get('notes');
        const shouldShow = notesParam === 'show';
        setIsNotesVisible(shouldShow);
    }, [location.search]);

    const toggleNotes = useCallback(() => {
        setIsNotesVisible(prev => !prev);
    }, []);

    const showNotes = useCallback(() => {
        setIsNotesVisible(true);
        // Update URL
        const newParams = new URLSearchParams(location.search);
        newParams.set('notes', 'show');
        const newSearch = newParams.toString();
        const newUrl = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
        navigate(newUrl, { replace: true });
    }, [location.pathname, location.search, navigate]);

    const hideNotes = useCallback(() => {
        setIsNotesVisible(false);
        // Update URL
        const newParams = new URLSearchParams(location.search);
        newParams.delete('notes');
        const newSearch = newParams.toString();
        const newUrl = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
        navigate(newUrl, { replace: true });
    }, [location.pathname, location.search, navigate]);

    const value = {
        isNotesVisible,
        toggleNotes,
        showNotes,
        hideNotes
    };

    return (
        <HiringNotesContext.Provider value={value}>
            {children}
        </HiringNotesContext.Provider>
    );
};
